import { PublicConfigSchema } from '@mist/contracts/api'
import type { PublicConfig } from '@mist/contracts/api'
import { getApi } from './api-client'

/**
 * Authoritative public launch configuration (Section 16.2, R-COMMERCE-01).
 *
 * `/api/v1/public/config` is the single source of truth for launch state, price
 * visibility, checkout visibility, and giveaway entry visibility. PostHog flags
 * can never override it. When the authority is unreachable we fall back to the
 * safest possible posture — everything gated off, price hidden — so the shell
 * can never expose commerce it cannot back.
 */
export const SAFE_PRELAUNCH_CONFIG: PublicConfig = {
  launchState: 'prelaunch',
  commerceAvailable: false,
  priceVisible: false,
  giveawayAvailable: false,
  version: 'safe-fallback',
  activeRules: null,
  cache: { maxAgeSeconds: 60, purgeAt: null },
  featureHints: { posthogMayExperiment: false, publicConfigAuthority: 'railway-postgresql' },
  contentSwitches: {},
}

export async function getPublicConfig(): Promise<PublicConfig> {
  try {
    return await getApi('/api/v1/public/config', PublicConfigSchema, { revalidateSeconds: 60 })
  } catch {
    return SAFE_PRELAUNCH_CONFIG
  }
}

/** True only when the authoritative config permits revealing price (R-COMMERCE-01). */
export function isPriceVisible(config: PublicConfig): boolean {
  return config.launchState === 'live' && config.priceVisible
}

/** True only when the authoritative config permits checkout (Section 8.4). */
export function isCommerceAvailable(config: PublicConfig): boolean {
  return config.launchState === 'live' && config.commerceAvailable
}
